import type {
  SearchFilters,
  SearchFilterSelection,
  SearchFilterType,
  SearchMultiSelectFilterType,
  SearchSelectFilterType,
  SearchValueFilterType,
  SelectedMultiOption,
  SelectedMultiOptionValues,
  SelectedOption,
  SelectedOptionValue,
  SelectedValue,
} from '@/scopes/search/search/infrastructure/api/types';
import { type DateOrRange, departureDateStore } from '@/infrastructure/departure-date/store';

import { useDependencies } from '@/scopes/common/infrastructure/dependencies';
import { clean } from '@/scopes/search/search/infrastructure/api/aiFiltersCleaner';
import { useSearchStore } from '@/scopes/search/search/infrastructure/store/useSearchStore';
import { useSearchDependencies } from '@/scopes/search/search/pages/SearchPage/context';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { difference, isEmpty, keys, partial } from 'ramda';
import * as React from 'react';
import { COMPULSORY_FILTER_TYPES } from './constants';
import { getMultiSelectFilterSelectedOption, getSelectFilterSelectedOption } from './getFilterOptions';

export function useAIFilterQuery() {
  const { searchStore, roomsParticipantsDistributionStore } = useDependencies();
  const { data } = useSearchQuery();
  const { userQuery, filterSelection, setIsAIFetching, setIsAIError, setUserQuery } = searchStore.getState();
  const { searchAPIClient, filterSelectionQueryParamHandler } = useSearchDependencies();

  const fetchAIFilters = async () => {
    setIsAIFetching(true);
    setIsAIError(false);

    try {
      const result = await searchAPIClient.getAIFilters(userQuery);
      const aiFilterSelection = clean(result, data?.filters);
      const newSelection = {
        ...filterSelection,
        ...aiFilterSelection,
      };

      if (aiFilterSelection.DepartureDate) {
        const { setDepartureDate, setOriginDate } = departureDateStore.getState();
        setOriginDate(
          Array.isArray(aiFilterSelection.DepartureDate)
            ? aiFilterSelection.DepartureDate[0]
            : aiFilterSelection.DepartureDate
        );
        setDepartureDate(aiFilterSelection.DepartureDate as DateOrRange);
      }
      if (aiFilterSelection.Participants) {
        const { setRooms } = roomsParticipantsDistributionStore.getState();
        setRooms(aiFilterSelection.Participants);
      }

      searchStore.setState({ filterSelection: newSelection });
      filterSelectionQueryParamHandler.update();
    } catch (error) {
      setUserQuery('');
      setIsAIError(true);
    } finally {
      setIsAIFetching(false);
    }
  };

  React.useEffect(() => {
    if (userQuery === '') return;
    fetchAIFilters();
  }, [userQuery]);
}

export function useSearchQuery() {
  const { searchAPIClient } = useSearchDependencies();
  const { searchStore } = useDependencies();
  const { pagination, sorting, filterSelection, isAIError, isAIFetching } = searchStore.getState();
  const queryParams = useSearchQueryParams();

  const searchCriteria = {
    pagination,
    sorting,
    filterSelection,
  };
  const filterInitialized = !isEmpty(filterSelection);

  const { isFetching, isLoading, ...rest } = useQuery({
    queryKey: ['search-results', queryParams],
    queryFn: () => searchAPIClient.getSearchResults(searchCriteria),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
    enabled: filterInitialized,
  });

  return {
    ...rest,
    isFetching: !filterInitialized || isFetching || isAIFetching,
    isLoading: !filterInitialized || isLoading,
    isAIError,
  };
}

export function useSearchQueryParams() {
  const { pagination, sorting, filterSelection } = useSearchStore();
  const { searchPageQueryBuilder } = useDependencies();

  return React.useMemo(
    () => searchPageQueryBuilder.build({ pagination, sorting, filterSelection }),
    [filterSelection, pagination, sorting, searchPageQueryBuilder]
  );
}

export function useResults() {
  const { isLoading, isFetching, data, error } = useSearchQuery();

  return {
    isLoading,
    isFetching,
    error,
    totalResults: data?.totalResults ?? 0,
    results: data?.results ?? [],
    accommodationTips: data?.accommodationTips ?? [],
    filteredCollection: data?.filteredCollection ?? null,
    ga4FilterCollection: data?.ga4FilterCollection ?? null,
    ga4Impressions: data?.ga4Impressions ?? [],
  };
}

export function usePagination() {
  const { isLoading, isFetching, data } = useSearchQuery();
  const { pagination, setNextPage, setPreviousPage, setPage } = useSearchStore();
  const { filterSelectionQueryParamHandler } = useSearchDependencies();
  const { paginationResolver } = useDependencies();
  const { getPages, resultsPerPage, currentPage, numberOfPages, isFirstPage, isLastPage, DOTS } = paginationResolver({
    pagination,
    totalResults: data?.totalResults,
  });

  const paginationRange: (string | number)[] = React.useMemo(() => getPages(), [getPages]);

  return {
    isLoading,
    isFetching,
    isFirstPage,
    isLastPage,
    pagination,
    currentPage,
    resultsPerPage,
    numberOfPages,
    paginationRange,
    setNextPage: () => {
      setNextPage();
      filterSelectionQueryParamHandler.update();
    },
    setPreviousPage: () => {
      setPreviousPage();
      filterSelectionQueryParamHandler.update();
    },
    setPage: (value: number | string) => {
      setPage(value);
      filterSelectionQueryParamHandler.update();
    },
    DOTS,
  };
}

export function useSorting() {
  const { isLoading, isFetching } = useSearchQuery();
  const { sorting, setSortingBy } = useSearchStore();
  const { filterSelectionQueryParamHandler } = useSearchDependencies();

  return {
    isLoading,
    isFetching,
    sorting,
    setSortingBy: (value: string) => {
      setSortingBy(value);
      filterSelectionQueryParamHandler.update();
    },
  };
}

export function useValueFilter<K extends SearchValueFilterType>(filterType: K) {
  const { isLoading, isFetching } = useSearchQuery();
  const { setFilterSelection, filterSelection, resetFilterValues } = useSearchStore();
  const { filterSelectionQueryParamHandler } = useSearchDependencies();

  return {
    isLoading,
    isFetching,
    selectedValue: filterSelection[filterType] as SelectedValue<SearchFilters[K]>,
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value);
      filterSelectionQueryParamHandler.update();
    },
    resetFilterValues: partial(resetFilterValues, [filterType]),
  };
}

export function useSelectFilter<K extends SearchSelectFilterType>(filterType: K) {
  const { isLoading, isFetching, data } = useSearchQuery();
  const { setFilterSelection, filterSelection } = useSearchStore();
  const { filterSelectionQueryParamHandler } = useSearchDependencies();

  const selectedOption = getSelectFilterSelectedOption(
    data?.filters ?? {},
    filterType,
    filterSelection[filterType] as SelectedOptionValue<SearchFilters[K]>
  );

  return {
    isLoading,
    isFetching,
    selectedOption,
    selectedValue: (filterSelection[filterType] as SelectedOptionValue<SearchFilters[K]>) ?? null,
    options: (data?.filters[filterType]?.options as SelectedOption<SearchFilters[K]>) ?? [],
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value);
      filterSelectionQueryParamHandler.update();
    },
  };
}

export function useMultiSelectFilter<K extends SearchMultiSelectFilterType>(filterType: K) {
  const { isLoading, isFetching, data } = useSearchQuery();
  const { setFilterSelection, filterSelection } = useSearchStore();
  const { filterSelectionQueryParamHandler } = useSearchDependencies();

  const selectedOptions = getMultiSelectFilterSelectedOption(
    data?.filters ?? {},
    filterType,
    (filterSelection?.[filterType] as SelectedMultiOptionValues<SearchFilters[K]>) ?? []
  );

  const filterSelectedValues = filterSelection[filterType];

  return {
    isLoading,
    isFetching,
    selectedOptions,
    selectedValues: (filterSelectedValues as SelectedMultiOptionValues<SearchFilters[K]>) ?? [],
    options: (data?.filters[filterType]?.options as SelectedMultiOption<SearchFilters[K]>) ?? [],
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value);
      filterSelectionQueryParamHandler.update();
    },
  };
}

export function useAiFilter() {
  const { setUserQuery } = useSearchStore();
  const { isFetching, isAIError } = useSearchQuery();
  return {
    setUserQuery,
    isFetching,
    isAIError,
  };
}

export function useFilters() {
  const { isLoading, isFetching } = useSearchQuery();
  const { resetFilterValues, filterSelection } = useSearchStore();

  const resetOptionalFilters = React.useCallback(() => {
    const allFilterTypes = keys(filterSelection);

    const filterTypesToReset = difference<SearchFilterType>(allFilterTypes, COMPULSORY_FILTER_TYPES);

    resetFilterValues(filterTypesToReset);
  }, [filterSelection, resetFilterValues]);

  return {
    isLoading,
    isFetching,
    resetOptionalFilters,
  };
}
